import React, { useState } from 'react';
import { User, Users, Loader2 } from 'lucide-react';
import { useJugadoresEquipoActivo } from '../hooks/useJugadoresEquipoActivo';
import { useRegistroFisico } from '../hooks/useRegistroFisico';
import { Card } from '../components/ui/Card';
import ModalDetalleJugador from '../components/modals/ModalDetalleJugador';
import { JugadorDto, RegistroFisicoDto } from '../types/jugador';

/**
 * Página que muestra la plantilla de jugadores del equipo activo del entrenador
 */
const TuPlantilla: React.FC = () => {
  const { jugadores, temporada, loading, error } = useJugadoresEquipoActivo();
  const { obtenerUltimoRegistro, loading: loadingRegistro } = useRegistroFisico();
  const [jugadorSeleccionado, setJugadorSeleccionado] = useState<JugadorDto | null>(null);
  const [ultimoRegistroFisico, setUltimoRegistroFisico] = useState<RegistroFisicoDto | null>(null);
  const [isModalDetalleOpen, setIsModalDetalleOpen] = useState(false);

  /**
   * Maneja el clic en una tarjeta de jugador
   */
  const handleClickJugador = async (jugador: JugadorDto) => {
    setJugadorSeleccionado(jugador);
    setIsModalDetalleOpen(true);
    
    // Obtener el último registro físico del jugador
    const registro = await obtenerUltimoRegistro(jugador.id);
    setUltimoRegistroFisico(registro);
  };

  /**
   * Maneja el cierre del modal de detalle
   */
  const handleCerrarModal = () => {
    setIsModalDetalleOpen(false);
    setJugadorSeleccionado(null);
    setUltimoRegistroFisico(null);
  };

  /**
   * Maneja cuando se actualiza un registro físico
   */
  const handleRegistroFisicoActualizado = async () => {
    // Recargar el último registro físico del jugador seleccionado
    if (jugadorSeleccionado) {
      const registro = await obtenerUltimoRegistro(jugadorSeleccionado.id);
      setUltimoRegistroFisico(registro);
    }
  };

  // Mostrar loading si está cargando jugadores o registros físicos
  if (loading || loadingRegistro) {
    return (
      <div className="flex-1 flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-8 h-8 animate-spin text-emerald-500 mb-4 mx-auto" />
          <p className="text-slate-400 font-poppins">
            {loading ? 'Cargando jugadores...' : 'Cargando registro físico...'}
          </p>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="flex-1 p-4 flex items-center justify-center">
        <div className="flex items-center">
          <Loader2 className="w-8 h-8 animate-spin text-emerald-500 mr-2" />
          <span className="text-slate-400 font-poppins">Cargando plantilla...</span>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex-1 p-4">
        <Card variant="default" className="p-4 border-red-500/20 bg-red-500/10">
          <div className="flex items-center">
            <div className="text-red-400">
              <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
              </svg>
            </div>
            <div className="ml-3">
              <h3 className="text-sm font-medium text-red-400 font-poppins">Error al cargar la plantilla</h3>
              <p className="text-sm text-red-300 mt-1">{error}</p>
            </div>
          </div>
        </Card>
      </div>
    );
  }

  return (
    <>
      <div className="flex-1 p-4">
        <div className="space-y-6">
          {/* Header */}
          <Card variant="default" className="p-6">
            <h1 className="text-2xl font-bold text-slate-50 mb-4 font-poppins">
              Tu Plantilla
            </h1>
            <p className="text-sm text-slate-400 font-poppins">
              Jugadores de tu equipo - {temporada?.nombre || 'Temporada Activa'}
            </p>
          </Card>
          
          {/* Contador de jugadores */}
          <Card variant="default" className="p-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center">
                <Users className="w-5 h-5 text-emerald-500 mr-2" />
                <span className="text-sm font-medium text-slate-300 font-poppins">
                  Total de jugadores: {jugadores.length}
                </span>
              </div>
            </div>
          </Card>
          
          {/* Lista de jugadores */}
          <div className="space-y-4">
            {jugadores.length === 0 ? (
              <Card variant="default" className="p-8">
                <div className="text-center">
                  <Users className="w-16 h-16 text-slate-500 mb-4 mx-auto" />
                  <h3 className="text-lg font-medium text-slate-300 mb-2 font-poppins">
                    No hay jugadores en tu plantilla
                  </h3>
                  <p className="text-slate-400 font-poppins">
                    Aún no tienes jugadores asignados a tu equipo para esta temporada.
                  </p>
                </div>
              </Card>
            ) : (
              jugadores.map((jugador) => (
                <Card 
                  key={jugador.id} 
                  variant="elevated"
                  className="p-4 hover:shadow-emerald-500/20 cursor-pointer relative transition-all duration-200 hover:scale-[1.02]"
                  onClick={() => handleClickJugador(jugador)}
                >
                  <div className="flex items-center">
                    <div className="flex-shrink-0">
                      <div className="w-12 h-12 bg-emerald-500/20 rounded-full flex items-center justify-center">
                        <User className="w-6 h-6 text-emerald-500" />
                      </div>
                    </div>
                    <div className="ml-4 flex-1 pr-16">
                      <h3 className="font-medium text-secondary-50">
                        {jugador.nombre} {jugador.apellido}
                      </h3>
                      <div className="mt-1">
                        <span className="text-sm text-slate-400 font-poppins">
                          <span className="font-medium text-emerald-400">Posición:</span> {jugador.posicion || 'No especificada'}
                        </span>
                      </div>
                    </div>
                    <div className="absolute top-4 right-4">
                      <span className="text-sm text-slate-400 font-poppins bg-slate-800/50 px-2 py-1 rounded">
                        {jugador.añoNacimiento}
                      </span>
                    </div>
                  </div>
                </Card>
              ))
            )}
          </div>
        </div>
      </div>

      {/* Modal de detalle del jugador */}
      <ModalDetalleJugador
        isOpen={isModalDetalleOpen}
        onClose={handleCerrarModal}
        jugador={jugadorSeleccionado}
        ultimoRegistroFisico={ultimoRegistroFisico}
        onRegistroFisicoActualizado={handleRegistroFisicoActualizado}
      />
    </>
  );
};

export default TuPlantilla;